import { getApi } from 'api';
import { AxiosError, AxiosResponse } from 'axios';
import { IApplicationLinkValidity, IApplicationSubmission } from 'types/employee/IJobApplicantApplication';
import { JobApplicantErrorKey } from 'types/employee/IJobApplicantLink';

const BASE_URL = 'JobApplicants/Links';

/**
 * Both calls are anonymous. getApi()'s interceptor only attaches an Authorization header when the
 * store says we are authenticated, so no separate client is needed.
 */
export const getApplicationLinkValidityRequest = async (token: string): Promise<IApplicationLinkValidity> => {
  const response: AxiosResponse<IApplicationLinkValidity> = await getApi().get(`${BASE_URL}/${token}/Validity`);
  return response.data;
};

/** Multipart is mandatory because of the file part — a JSON body is rejected with 415. */
export const buildApplicationFormData = (submission: IApplicationSubmission): FormData => {
  const formData = new FormData();

  formData.append('FirstName', submission.firstName);
  formData.append('LastName', submission.lastName);
  formData.append('Email', submission.email);
  formData.append('Phone', submission.phone);
  formData.append('MotivationalText', submission.motivationalText);
  formData.append('PrivacyStatementAccepted', String(submission.privacyStatementAccepted));
  formData.append('LanguageId', String(submission.languageId));

  if (submission.cv) {
    formData.append('Cv', submission.cv);
  }

  return formData;
};

export const submitApplicationRequest = async (token: string, submission: IApplicationSubmission): Promise<void> => {
  await getApi().post(`${BASE_URL}/${token}/Applications`, buildApplicationFormData(submission), {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export type ApplicationFieldName =
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'phone'
  | 'motivationalText'
  | 'privacyStatementAccepted';

/** ValidationProblemDetails keys are .NET model property names; this maps them back to the form. */
const FIELD_BY_MODEL_PROPERTY: Record<string, ApplicationFieldName> = {
  firstname: 'firstName',
  lastname: 'lastName',
  email: 'email',
  phone: 'phone',
  motivationaltext: 'motivationalText',
  privacystatementaccepted: 'privacyStatementAccepted',
};

export type PublicApplicationFailure =
  | { kind: 'errorKey'; key: JobApplicantErrorKey; fileName?: string }
  | { kind: 'fieldErrors'; fields: ApplicationFieldName[] }
  /** Bare 413 from Kestrel — no body, so there is no key to translate. */
  | { kind: 'tooLarge' }
  | { kind: 'unknown' };

interface ErrorResponseBody {
  error?: string;
  details?: { fileName?: string } | null;
}

interface ValidationProblemBody {
  errors?: Record<string, string[]>;
}

/**
 * Three response shapes, so this endpoint gets its own parser rather than reusing
 * parseJobApplicantError — that one folds any ModelState failure onto BRANCH_REQUIRED, which is
 * wrong for a form whose fields are not locationIds.
 */
export const parsePublicApplicationError = (error: unknown): PublicApplicationFailure => {
  const axiosError = error as AxiosError<ErrorResponseBody & ValidationProblemBody>;
  const status = axiosError?.response?.status;
  const body = axiosError?.response?.data;

  // First: Kestrel rejects an oversized body before application code runs, so there is no body.
  if (status === 413) {
    return { kind: 'tooLarge' };
  }

  if (body?.error) {
    return {
      kind: 'errorKey',
      key: body.error as JobApplicantErrorKey,
      fileName: body.details?.fileName,
    };
  }

  if (body?.errors) {
    const fields = Object.keys(body.errors)
      .map(property => FIELD_BY_MODEL_PROPERTY[property.toLowerCase()])
      .filter(Boolean);
    if (fields.length > 0) {
      return { kind: 'fieldErrors', fields };
    }
  }

  return { kind: 'unknown' };
};
